import { useMemo } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { LabelIcon } from '@icons/LabelIcon';
import { SearchIcon } from '@icons/SearchIcon';
import { HashTagIcon } from '@icons/HashTagIcon';
import { PeopleIcon } from '@icons/PeopleIcon';
import { TextIcon } from '@icons/TextIcon';
import { IDropdownOption, Option } from '@components/Dropdown';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { useIntegrationsContext } from '@insights_module/context/IntegrationsContext';
import { RateIcon } from '@icons/RateIcon';
import { CalendarIcon } from '@icons/CalendarIcon';
import { StarIcon } from '@icons/StarIcon';
import { useFeatureFlag, FEATURE_FLAG } from '@auth_module/hooks/useFeatureFlag';
import { LanguageIcon } from '@icons/LanguageIcon';

const TypeItem = tw.div`flex items-center justify-start px-2 py-1 cursor-pointer gap-[7px] text-xs whitespace-nowrap z-[1000]`;
const IconWrapper = tw.div`flex justify-center items-center min-w-[16px] min-h-[16px]`;

export const useFilterTypeOptions = () => {
  const { t } = useTranslation();
  const { selectedTableType } = useInsightsContext();
  const { type: integrationType } = useIntegrationsContext();

  const { isEnabled: isNELEnabled } = useFeatureFlag(FEATURE_FLAG.NAMED_ENTITY_LINKING);
  const { isEnabled: isEpisodesByShowIdEnabled } = useFeatureFlag(
    FEATURE_FLAG.FILTER_EPISODES_BY_SHOW_ID
  );
  const { isEnabled: isLanguageFilterEnabled } = useFeatureFlag(FEATURE_FLAG.AQL_LANGUAGE_FILTER);
  const dropdownOptions: IDropdownOption[] = useMemo(
    () =>
      [
        {
          value: 'apple_categories',
          meta: { for: ['shows'] },
          label: (
            <TypeItem>
              <IconWrapper>
                <LabelIcon tw='fill-accent-muted' />
              </IconWrapper>
              {t('insights.query_builder.types.apple_categories')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.apple_categories')}</Option>,
        },
        {
          value: 'author',
          meta: { for: ['shows'] },
          label: (
            <TypeItem>
              <IconWrapper>
                <PeopleIcon tw='stroke-accent-muted' />
              </IconWrapper>
              {t('insights.query_builder.types.author')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.author')}</Option>,
        },
        {
          value: 'duration',
          meta: { for: ['episodes'] },
          label: (
            <TypeItem>
              <IconWrapper>
                <HashTagIcon tw='stroke-accent-muted' />
              </IconWrapper>
              {t('insights.query_builder.types.duration')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.duration')}</Option>,
        },
        {
          value: 'title',
          meta: { for: ['episodes'] },
          label: (
            <TypeItem>
              <IconWrapper>
                <TextIcon tw='fill-accent-muted' />
              </IconWrapper>
              {t('insights.query_builder.types.title')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.title')}</Option>,
        },
        {
          value: 'language',
          meta: { for: ['episodes'], isEnabled: isLanguageFilterEnabled },
          label: (
            <TypeItem>
              <IconWrapper>
                <LanguageIcon tw='stroke-accent-muted' />
              </IconWrapper>
              {t('insights.query_builder.types.language')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.language')}</Option>,
        },
        {
          value: 'audio_count',
          meta: { for: ['shows'] },
          label: (
            <TypeItem>
              <IconWrapper>
                <HashTagIcon tw='stroke-accent-muted' />
              </IconWrapper>
              {t('insights.query_builder.types.audio_count')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.audio_count')}</Option>,
        },
        {
          value: 'contextual',
          meta: { for: ['shows', 'episodes'] },
          label: (
            <TypeItem>
              <IconWrapper>
                <LabelIcon tw='fill-accent-muted' />
              </IconWrapper>
              {t('insights.query_builder.types.iab')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.iab')}</Option>,
        },
        {
          value: 'named_entities',
          meta: { for: ['shows', 'episodes'] },
          label: (
            <TypeItem>
              <IconWrapper>
                <SearchIcon tw='stroke-accent-muted w-3 h-3.5' />
              </IconWrapper>
              {t('insights.query_builder.types.named_entity')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.named_entity')}</Option>,
        },
        {
          value: 'linked_named_entities',
          meta: { for: ['shows', 'episodes'], isEnabled: isNELEnabled },
          label: (
            <TypeItem>
              <IconWrapper>
                <SearchIcon tw='stroke-accent-muted w-3 h-3.5' />
              </IconWrapper>
              {t('insights.query_builder.types.linked_named_entities')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.linked_named_entities')}</Option>,
        },
        {
          value: 'name',
          meta: { for: ['shows'] },
          label: (
            <TypeItem>
              <IconWrapper>
                <TextIcon tw='fill-accent-muted' />
              </IconWrapper>
              {t('insights.query_builder.types.name')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.name')}</Option>,
        },
        {
          value: 'podcast_type',
          meta: { for: ['shows'] },
          label: (
            <TypeItem>
              <IconWrapper>
                <LabelIcon tw='fill-accent-muted' />
              </IconWrapper>
              {t('insights.query_builder.types.podcast_type')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.podcast_type')}</Option>,
        },
        {
          value: 'published_date',
          meta: { for: ['episodes'] },
          label: (
            <TypeItem>
              <IconWrapper>
                <CalendarIcon tw='stroke-accent-muted' />
              </IconWrapper>
              {t('insights.query_builder.types.published_date')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.published_date')}</Option>,
        },
        {
          value: 'transcript',
          meta: { for: ['episodes'] },
          label: (
            <TypeItem>
              <IconWrapper>
                <TextIcon tw='fill-accent-muted' />
              </IconWrapper>
              {t('insights.query_builder.types.transcript')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.transcript')}</Option>,
        },
        {
          value: 'episodes_by_show_id',
          meta: { for: ['episodes'], isEnabled: isEpisodesByShowIdEnabled },
          label: (
            <TypeItem>
              <IconWrapper>
                <div tw='text-accent-muted'>ID</div>
              </IconWrapper>
              {t('insights.query_builder.types.episodes_by_show_id')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.episodes_by_show_id')}</Option>,
        },
        {
          value: 'brand_safety_garm',
          meta: { for: ['shows', 'episodes'] },
          label: (
            <TypeItem>
              <IconWrapper>
                <RateIcon tw='fill-accent-muted' />
              </IconWrapper>
              {t('insights.query_builder.types.garm')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.garm')}</Option>,
        },
        {
          value: 'apple_star_rating',
          meta: { for: ['shows'], read_only: true },
          label: (
            <TypeItem>
              <IconWrapper>
                <StarIcon tw='stroke-accent' />
              </IconWrapper>
              {t('insights.query_builder.types.apple_star_rating')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.apple_star_rating')}</Option>,
        },
        {
          value: 'apple_rating_count',
          meta: { for: ['shows'], read_only: true },
          label: (
            <TypeItem>
              <IconWrapper>
                <HashTagIcon tw='stroke-accent' />
              </IconWrapper>
              {t('insights.query_builder.types.apple_rating_count')}
            </TypeItem>
          ),
          selectedLabel: <Option>{t('insights.query_builder.types.apple_rating_count')}</Option>,
        },
      ].filter(
        ({ meta }) =>
          meta?.for && meta.for.includes(integrationType ? 'episodes' : selectedTableType)
      ),
    [selectedTableType, integrationType]
  );

  return {
    // filter out options that are disabled by feature flag
    dropdownOptions: dropdownOptions.filter(
      ({ meta }) => meta?.isEnabled === undefined || meta.isEnabled
    ),
    options: dropdownOptions.map(({ value }) => value) as string[],
    allOptions: dropdownOptions,
  };
};
